import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { InputLabel, Typography, useTheme } from '@mui/material';
//
import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Iconify from '../iconify';
//
import { UploadProps } from './types';
import RejectionFiles from './errors-rejection-files';
import { fData } from '../../utils/format-number';

// ----------------------------------------------------------------------
type Props = UploadProps & {
  onDropLogic?: (aF: any[]) => Promise<any>;
};

export default function UploadCSV({
  error,
  file,
  disabled,
  placeholder,
  helperText,
  maxSize = 1048576,
  sx,
  onDropLogic = (aF: any[]) => Promise.resolve(),
  ...other
}: Props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState<File[]>([]);
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    disabled,
    maxFiles: 1,
    maxSize,
    accept: {
      'text/csv': ['.csv'],
    },
    onDrop: (aF, fR, e) => {
      setLoading(true);
      setFileData(aF);
      if (aF.length && onDropLogic) {
        onDropLogic(aF).then(() => {
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    },
    ...other,
  });

  const hasFile = !!file;

  const hasError = isDragReject || !!error;

  const renderPlaceholder = (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1}
      className="upload-placeholder"
      sx={{
        width: 1,
        height: 1,
        zIndex: 9,
        borderRadius: 1,
        color: 'text.disabled',
        bgcolor: alpha(theme.palette.grey[500], 0.08),
        transition: theme.transitions.create(['opacity'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          opacity: 0.72,
        },
        ...(hasError && {
          color: 'error.main',
          bgcolor: alpha(theme.palette.error.main, 0.08),
        }),
        ...(hasFile && {
          zIndex: 9,
          opacity: 0,
          color: 'common.white',
          bgcolor: alpha(theme.palette.grey[900], 0.64),
        }),
      }}
    >
      {!hasFile && <Iconify icon="eva:file-add-fill" width="30px" />}
    </Stack>
  );

  return (
    <>
      {placeholder && (
        <InputLabel
          sx={{
            px: 1,
            fontSize: '0.8rem',
            fontFamily: 'Public Sans, sans-serif',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {placeholder}
        </InputLabel>
      )}
      <Box
        {...getRootProps()}
        sx={{
          p: 0,
          width: 1,
          height: '80px',
          cursor: 'pointer',
          overflow: 'hidden',
          borderRadius: 1,
          border: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          ...(hasError && {
            borderColor: 'error.main',
          }),
          ...sx,
        }}
      >
        {!hasFile && <input {...getInputProps()} />}
        {loading && (
          <Stack width={1} height={1} alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        )}
        {!hasFile && !loading && renderPlaceholder}
        {hasFile &&
          fileData.length > 0 &&
          fileData.map((f) => (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                width: 1,
                height: 1,
                px: 2,
                py: 1,
                bgcolor: alpha(theme.palette.grey[900], 0.64),
                color: 'common.white',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="eva:file-fill" width="20px" />
                <Typography variant="subtitle2" noWrap>
                  {f.name} - {f.size ? fData(f.size) : ''}
                </Typography>
              </Stack>
            </Stack>
          ))}
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            width: 1,
            textAlign: 'start',
            color: 'text.disabled',
            mb: 2,
          }}
        >
          {helperText}
        </Typography>
      )}

      <RejectionFiles fileRejections={[...fileRejections]} maxSize={maxSize} />
    </>
  );
}
